import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ClipboardList, Copy, Edit2, Loader2, Plus, Trash2 } from 'lucide-react';
import { apiDel, apiGet, apiPost, apiPut } from '../../lib/api.js';
import { fillScript } from '../../lib/scriptVars.js';
import { useAuth } from '../../store/auth.js';

const SUBTABS = [
  { id: 'personal', label: 'My Call Scripts' },
  { id: 'company', label: 'Company Call Scripts' },
];

function Editor({ initial, busy, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  const field = 'w-full rounded-lg bg-civix-panelLight border border-civix-border px-3 py-2 text-xs text-white/90 focus:border-civix-purple/60 outline-hidden';
  return (
    <div className="rounded-lg border border-civix-purple/40 bg-civix-panel p-3 space-y-2">
      <input className={field} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Script title (e.g. Soft Close)" maxLength={120} />
      <textarea className={`${field} resize-y min-h-[110px]`} value={body} onChange={(e) => setBody(e.target.value)}
        placeholder="Script text. Variables: {name} {agent} {state} {city} {plan}" />
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost text-xs" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn-primary text-xs flex items-center gap-1.5" disabled={busy || !title.trim() || !body.trim()}
          onClick={() => onSave({ title: title.trim(), body })}>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save script
        </button>
      </div>
    </div>
  );
}

/**
 * My / Company call-script browser with lead-variable interpolation. Shared by
 * the Lead Detail tab and the pop-out window.
 * @param {Object} props
 * @param {object|null} props.lead - Lead whose details fill {name}, {state}, ...
 * @param {boolean} [props.large] - Bigger type for the pop-out window.
 * @returns {JSX.Element}
 */
export default function ScriptViewer({ lead, large = false }) {
  const qc = useQueryClient();
  const agent = useAuth((s) => s.agent);
  const isAdmin = ['admin', 'superadmin'].includes(agent?.role);
  const [scope, setScope] = useState('personal');
  const [editing, setEditing] = useState(null); // script | 'new' | null

  const { data, isLoading } = useQuery({ queryKey: ['call-scripts'], queryFn: () => apiGet('/call-scripts'), staleTime: 30_000 });
  const scripts = data?.[scope] || [];
  const canEdit = scope === 'personal' || isAdmin;

  const save = useMutation({
    mutationFn: ({ id, ...body }) => (id ? apiPut(`/call-scripts/${id}`, body) : apiPost('/call-scripts', { scope, ...body })),
    onSuccess: () => { setEditing(null); qc.invalidateQueries({ queryKey: ['call-scripts'] }); toast.success('Script saved'); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not save script'),
  });
  const remove = useMutation({
    mutationFn: (id) => apiDel(`/call-scripts/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['call-scripts'] }); toast.success('Script deleted'); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not delete script'),
  });

  async function copy(text) {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); } catch { toast.error('Could not copy'); }
  }

  return (
    <div className="space-y-3">
      <div className="flex rounded-xl border border-civix-border bg-civix-panelLight p-1" role="tablist">
        {SUBTABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={scope === t.id} onClick={() => { setScope(t.id); setEditing(null); }}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${scope === t.id ? 'bg-civix-purple/25 text-white border border-civix-purple/50' : 'text-white/55 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {canEdit && editing !== 'new' && (
        <button type="button" className="text-xs text-civix-purpleSoft hover:text-white flex items-center gap-1" onClick={() => setEditing('new')}>
          <Plus className="w-3.5 h-3.5" /> New {scope === 'company' ? 'company' : 'personal'} script
        </button>
      )}
      {editing === 'new' && <Editor busy={save.isPending} onCancel={() => setEditing(null)} onSave={(v) => save.mutate(v)} />}

      {isLoading && <p className="text-xs text-white/40 p-3">Loading scripts…</p>}
      {!isLoading && scripts.length === 0 && (
        <p className="text-xs text-white/40 p-3">
          {scope === 'personal' ? 'You have no personal scripts yet. Add your own talk tracks.' : 'No company scripts yet.'}
        </p>
      )}

      {scripts.map((s) => (editing?._id === s._id ? (
        <Editor key={s._id} initial={s} busy={save.isPending} onCancel={() => setEditing(null)} onSave={(v) => save.mutate({ id: s._id, ...v })} />
      ) : (
        <article key={s._id} className="rounded-lg bg-civix-panel border border-civix-border p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <h4 className="flex items-center gap-2 text-xs font-semibold text-white/90">
              <ClipboardList className="w-4 h-4 text-civix-purpleSoft" /> {s.title}
            </h4>
            <div className="flex items-center gap-2 text-white/40 shrink-0">
              <button type="button" onClick={() => copy(fillScript(s.body, lead, agent))} title="Copy to clipboard" aria-label="Copy script" className="hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
              {canEdit && !s.builtin && (
                <>
                  <button type="button" onClick={() => setEditing(s)} title="Edit" aria-label="Edit script" className="hover:text-white"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => { if (window.confirm(`Delete "${s.title}"?`)) remove.mutate(s._id); }} title="Delete" aria-label="Delete script" className="hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
          </div>
          <p className={`${large ? 'text-base' : 'text-[11px]'} text-white/70 leading-relaxed whitespace-pre-wrap`}>{fillScript(s.body, lead, agent)}</p>
        </article>
      )))}
    </div>
  );
}
